import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ReactHookForm } from '../../../components/hook-form/react-hook-form.component';
import { useReactHookForm } from '../../../hooks/useReactHookForm';
import { CreatePlanForm } from './create-plan.form';
import { SelectResult } from '../../../components/select/select-chain.component';
import { MutationKeyConfig } from '../../../api/api-helper';
import { InvalidateRoute } from '../../../hooks/useDeleteData';
import { useState } from 'react';
import { useSaveData } from '../../../hooks/useSaveData';
import { useAuthorize } from '../../../hooks/use-authorize';
import { UserPermissionName } from '../../../models/user-permission-name';

interface CreatePlanDialog {
  data: any;
  title: string;
  yupSchema?: any;
  onSubmitFilters: (filter: SelectResult) => void;
  onClosed: () => void;
  config: { saveConfig: MutationKeyConfig; invalidateRoute?: InvalidateRoute };
}

export const CreatePlanDialog = ({
  data,
  title,
  yupSchema,
  onSubmitFilters,
  onClosed,
  config: { saveConfig, invalidateRoute },
}: CreatePlanDialog) => {
  const form = useReactHookForm({
    defaultValues: {
      ...data,
      customer: Number(data?.customerID) ? { customerID: Number(data?.customerID), customerName: '' } : null,
      office: Number(data?.officeID) ? { officeID: Number(data?.officeID), officeName: '' } : null,
      sqFootage: data?.area ?? '',
      volume: data?.volume ?? '',
      worstcaseScore: data?.worstcaseScore ?? '',
      windowArea: data?.windowArea ?? '',
      associatedPlan: { planID: Number(data?.planAssociatedWithPlanID), planName: '' },
      wallHeight: data?.wallHeight ?? '',
      uploadedFile: data?.fileName ?? '',
      uploadedFileType: data?.fileType ?? '',
    },
    yupSchema,
  });

  const [fileData, setFileData] = useState<File>();
  const [isFileDeleted, setFileDeleted] = useState<boolean>(false);
  const { isLoading, isError, error, mutateAsync } = useSaveData(saveConfig, invalidateRoute);
  const [planArgs, setPlanArgs] = useState<any>({
    customerID: Number(data?.customerID) ?? '',
    officeID: Number(data?.officeID) ?? '',
  });

  const getExtension = (fileName: string) => {
    const extension = "." + fileName?.split('.').pop();
    return extension;
  }

  const handleSubmit = async (value: any) => {
    const formData = new FormData();
    formData.append('PlanID', value?.planID ?? '');
    formData.append('PlanName', value?.planName ?? '');
    formData.append('customerID', value?.customer?.customerID ?? '');
    formData.append('officeID', value?.office?.officeID ?? '');
    formData.append('UploadedFile', fileData ?? '');
    formData.append('PlanFileName', fileData ? fileData?.name : value?.uploadedFile ?? '');
    formData.append('PlanFileType', fileData ? getExtension(fileData?.name) : value?.uploadedFile ? getExtension(value?.uploadedFile) : "");
    formData.append('PlanFileTypeID', value?.planFileTypeID ?? '');
    formData.append('Area', value?.sqFootage ?? '');
    formData.append('Volume', value?.volume ?? '');
    formData.append('WorstcaseScore', value?.worstcaseScore ?? '');
    formData.append('WindowArea', value?.windowArea ?? '');
    formData.append('WallHeight', value?.wallHeight ?? '');
    formData.append('AssociatedPlanID', value?.associatedPlan?.planID);
    if (isFileDeleted) formData.append('IsFileDeleted', isFileDeleted.toString());

    await mutateAsync({ data: formData });
    onSubmitFilters({
      customerID: value?.customer?.customerID,
      officeID: value?.office?.officeID ?? '',
    });
    onClosed();
  };

  const handleUploadFile = (file: File) => {
    setFileData(file);
    setFileDeleted(false);
    if (data?.fileName && !file) setFileDeleted(true)
  };

  const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

  return (
    <Dialog
      open={true}
      fullWidth={true}
      maxWidth={'xl'}
    //  onClose={onClosed}
    >
      <DialogTitle id="form-dialog-title">{title}</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClosed}
        sx={{
          position: 'absolute',
          right: 10,
          top: 15,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <Tooltip title={<Typography variant="body1">Close</Typography>}>
          <CloseIcon />
        </Tooltip>
      </IconButton>
      <ReactHookForm formName="SAVE_PLAN_LIBRARY_FORM" onSubmit={handleSubmit} formContextProps={form} isBusy={isLoading}>
        <DialogContent dividers className="popupDilog">
          <CreatePlanForm isEdit={!!data?.planID} uploadFile={handleUploadFile} planRequestArgs={planArgs} />
        </DialogContent>
        <div style={{ padding: '5px', paddingLeft: '16px', paddingRight: '16px' }}>
          <DialogActions className="spaceBetween">
            <Button variant="outlined" style={{ width: '130px' }} onClick={onClosed}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" style={{ width: '130px' }} disabled={isReadOnlyPermission}>
              Save
            </Button>
          </DialogActions>
        </div>
      </ReactHookForm>
    </Dialog>
  );
};
